/**
 * SopralluogoStoryPreview — animazione multi-scena per il phone mockup
 * dell'hero. Ricalca il VERO flusso del sopralluogo dell'app: stepper in
 * cima "Racconta → Fotografa → Genera", scene che avanzano insieme.
 * iter v33 r78.
 *
 * User feedback (dopo iter r77):
 * «Fa schifo, e deve sembrare lo stesso appartamento. E deve essere bella
 * tutta l'animazione: deve funzionare esattamente come funziona il nostro
 * sopralluogo. Deve colpire».
 *
 * Approccio:
 *  1. Stepper reale (come RecordingPhase.jsx) sempre visibile in alto
 *  2. Foto reali dello STESSO appartamento (Brera, parquet chevron, palette
 *     beige/salvia) — cucina, sala, camera, bagno
 *  3. Scene che ricalcano l'esperienza vera:
 *     - Scene RACCONTA:   header + REC button + waveform live + bolle
 *                         trascrizione
 *     - Scene FOTOGRAFA:  griglia che si POPOLA dinamicamente (foto 1
 *                         appare, 2 appare, 3, 4) + "Migliora" + crossfade
 *                         before→after + riordina
 *     - Scene GENERA:     spinner "Sto creando l'annuncio…"
 *     - Scene VETRINA:    vetrina completa con hero, prezzo, specs,
 *                         gallery, portali
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Microphone,
  Camera,
  Sparkle,
  Check,
  ArrowRight,
  Share,
  Record,
  Stop,
  Spinner,
  InstagramLogo,
  Heart,
  Play,
  UserCircle,
  House,
  HouseSimple,
  Calendar,
  WhatsappLogo,
  UsersFour,
  ChatCircle,
  FileText,
  Couch,
} from "@phosphor-icons/react";

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const TRANSCRIPT = [
  "Trilocale al terzo piano, palazzo signorile…",
  "95 mq, ristrutturato, parquet rovere.",
  "Prezzo richiesto: 245 mila.",
];

// 4 coppie DELLO STESSO APPARTAMENTO (Brera ristrutturato, parquet chevron,
// palette beige/salvia). Foto generate con Nano Banana (iter r79) da un
// prompt "DNA" comune per garantire coerenza fra le stanze — parquet spina
// francese chiaro, finestre in ferro nero, muri panna, luce naturale calda.
// Il "before" e' fotografato dal telefono dell'agente con oggetti realistici
// nella scena (canovaccio, coperta, libro aperto) — verosimile, non caotico.
const PHOTOS = [
  { before: "/landing/apt/cucina-before.jpg", after: "/landing/apt/cucina-after.jpg", room: "Cucina" },
  { before: "/landing/apt/living-before.jpg", after: "/landing/apt/living-after.jpg", room: "Soggiorno" },
  { before: "/landing/apt/camera-before.jpg", after: "/landing/apt/camera-after.jpg", room: "Camera" },
  { before: "/landing/apt/bagno-before.jpg",  after: "/landing/apt/bagno-after.jpg",  room: "Bagno" },
];

/* ------------------------------------------------------------------ */
/* Stepper (sempre visibile in cima, avanza al variare di stepIdx)     */
/* ------------------------------------------------------------------ */
const STEPS = [
  { key: "record",  label: "Racconta" },
  { key: "photo",   label: "Foto" },
  { key: "vetrina", label: "Vetrina" },
  { key: "social",  label: "Social" },
  { key: "servizi", label: "Servizi" },
];

// iter r99l · User M1527 — Scene finale «Servizi»: griglia 2 col di servizi
// ESATTI dalla presentazione allegata (foto migliorate, post e reel social,
// sito personale, vetrina immobiliare, stima online, agenda vocale, whatsapp,
// contatti & CRM, assistente immobiliare, pubblica sui portali).
//
// iter 2026-02 · User: coerenza col Reel promo aggiornato. Aggiunto
// «Rendering & Home Staging» in posizione 2 (dopo Foto migliorate), stesso
// linguaggio grafico degli altri (icona Couch + colore terra sabbia). La
// griglia diventa 11 items: 2 col × 5 row + 1 item spaiato sull'ultima
// row (accettabile — non creiamo card speciali per la nuova voce).
//
// Ogni card: icona colorata cerchio + titolo bold + micro descrizione.
// Appaiono staggered una alla volta.
const SERVIZI = [
  { icon: Camera,        color: "#7C8B6E", title: "Foto migliorate",           desc: "In un tocco, senza Photoshop." },
  { icon: Couch,         color: "#B08968", title: "Rendering & Home Staging",  desc: "Spazi vuoti, restyling e cambio stile." },
  { icon: InstagramLogo, color: "#DD2A7B", title: "Post & reel social",        desc: "Post, storie e reel già pronti." },
  { icon: UserCircle,    color: "#3E7EC7", title: "Sito personale",            desc: "Il tuo sito con tutti gli annunci." },
  { icon: House,         color: "#E08D3E", title: "Vetrina immobiliare",       desc: "Mini-sito per ogni annuncio." },
  { icon: HouseSimple,   color: "#6B6A65", title: "Stima online",              desc: "Link o QR per la stima." },
  { icon: Calendar,      color: "#D14C3D", title: "Agenda vocale",             desc: "Detti, Pronto fa tutto." },
  { icon: WhatsappLogo,  color: "#25D366", title: "WhatsApp",                  desc: "Auguri, follow-up, campagne." },
  { icon: UsersFour,     color: "#4A4948", title: "Contatti & CRM",            desc: "Clienti, budget, preferenze." },
  { icon: ChatCircle,    color: "#E68A2C", title: "Assistente immobiliare",    desc: "Dubbi, bozze, spunti." },
  { icon: FileText,      color: "#4A4948", title: "Pubblica sui portali",      desc: "Estensione Chrome." },
];

function Stepper({ stepIdx }) {
  return (
    <div className="flex items-center gap-1" data-testid="sst-stepper">
      {STEPS.map((s, i) => {
        const active = i === stepIdx;
        const done = i < stepIdx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex items-center gap-1 shrink-0">
              <motion.span
                animate={{
                  scale: active ? 1.05 : 1,
                  backgroundColor: done
                    ? "#7C8B6E"
                    : active
                    ? "#1C1C1A"
                    : "#EAE7DF",
                  color: done || active ? "#FFFFFF" : "#9A968B",
                }}
                transition={{ duration: 0.3 }}
                className="w-4 h-4 rounded-full text-[9px] font-bold inline-flex items-center justify-center"
              >
                {done ? <Check size={8} weight="bold" /> : i + 1}
              </motion.span>
              {active && (
                <motion.span
                  key={`label-${s.key}`}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#1C1C1A] whitespace-nowrap overflow-hidden"
                >
                  {s.label}
                </motion.span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <span
                className="flex-1 h-px min-w-[10px]"
                style={{
                  background: i < stepIdx ? "#7C8B6E" : "#EAE7DF",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Waveform                                                            */
/* ------------------------------------------------------------------ */
function Waveform({ active = true }) {
  const heights = [8, 16, 22, 12, 18, 26, 16, 10, 20, 28, 18, 12, 22, 20, 14, 24, 16, 10, 20, 24];
  return (
    <div className="flex items-end gap-[2.5px] h-6" aria-hidden="true">
      {heights.map((h, i) => (
        <span
          key={i}
          className="w-[2.5px] rounded-full bg-[#7C8B6E] origin-bottom"
          style={{
            height: `${h}px`,
            opacity: 0.55 + (i % 5) * 0.09,
            animation: active
              ? `sst-bar 0.6s ease-in-out ${i * 35}ms infinite alternate`
              : "none",
          }}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Scene · RACCONTA (recording)                                        */
/* ------------------------------------------------------------------ */
function SceneRecord({ seconds }) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return (
    <motion.div
      key="record"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="absolute inset-0 px-3.5 pt-2 pb-3 flex flex-col"
      data-testid="sst-scene-record"
    >
      {/* Address bar */}
      <div className="text-center mb-3">
        <div className="text-[9px] uppercase tracking-[0.22em] text-[#9A968B] font-bold">
          Sopralluogo
        </div>
        <div className="mt-0.5 inline-flex items-center gap-1 text-[11.5px] text-[#1C1C1A] font-medium">
          <MapPin size={11} weight="fill" className="text-[#7C8B6E]" />
          Via Solferino · Brera
        </div>
      </div>

      {/* REC bubble + timer */}
      <div className="flex flex-col items-center mb-4 mt-1">
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: [0.95, 1.05, 0.95] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-16 h-16 rounded-full bg-[#C13533] flex items-center justify-center shadow-[0_6px_18px_rgba(193,53,51,0.4)]"
        >
          <Stop size={22} weight="fill" className="text-white" />
        </motion.div>
        <div className="mt-2 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C13533] animate-pulse" />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#C13533]">
            Registrando
          </span>
          <span className="text-[10.5px] tabular-nums text-[#3F3D38] font-mono ml-1">
            {mm}:{ss}
          </span>
        </div>
        <div className="mt-1.5"><Waveform active /></div>
      </div>

      {/* Trascrizione live */}
      <div className="text-center mb-1.5">
        <span className="text-[8.5px] uppercase tracking-[0.22em] text-[#9A968B] font-bold inline-flex items-center gap-1">
          <Microphone size={9} weight="fill" /> Trascrizione live
        </span>
      </div>
      <div className="space-y-1.5 flex-1">
        {TRANSCRIPT.map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.6 + i * 0.8, duration: 0.35, ease: "easeOut" }}
            className="bg-[#F1F4ED] rounded-md px-3 py-1.5 text-[10.5px] text-[#1C1C1A]/90 border border-[#EAE7DF]"
          >
            {t}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Scene · FOTOGRAFA (photos)                                          */
/* ------------------------------------------------------------------ */
function ScenePhoto({ phase }) {
  // phase: 0    = scatto foto 1/4
  //        0.5  = scatto foto 2/4
  //        0.75 = scatto foto 3/4
  //        0.9  = scatto foto 4/4 (tutte visibili, "Sto scattando 4/4")
  //        1    = tap "Migliora le foto"
  //        2    = enhancing (progress bar)
  //        3    = migliorate (after crossfade completato)
  //        4    = tap "Crea annuncio" (finale · va poi a scene generate)
  // iter r88 · User: «fai fare 4/4 (non 3/4) e togli il riordino, metti
  // solo un chiaro "premi e crea annuncio"». Rimosse phase 5.
  const shotsCount = phase === 0 ? 1
    : phase === 0.5 ? 2
    : phase === 0.75 ? 3
    : 4; // >= 0.9 · 4/4 fisso

  const showAfter = phase >= 3;
  const INITIAL_ORDER = [3, 2, 1, 0];    // ordine iniziale foto
  const currentOrder = INITIAL_ORDER;

  return (
    <motion.div
      key="photo"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="absolute inset-0 px-3.5 pt-2 pb-3 flex flex-col"
      data-testid="sst-scene-photo"
    >
      {/* Address bar */}
      <div className="text-center mb-3">
        <div className="text-[9px] uppercase tracking-[0.22em] text-[#9A968B] font-bold">
          Sopralluogo · Foto
        </div>
        <div className="mt-0.5 text-[11.5px] text-[#1C1C1A] font-medium inline-flex items-center gap-1">
          <Camera size={11} weight="fill" className="text-[#7C8B6E]" />
          {shotsCount}/4 stanze
        </div>
      </div>

      {/* Griglia 2x2 · foto appaiono dinamicamente */}
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        {currentOrder.map((originalIdx, gridIdx) => {
          const p = PHOTOS[originalIdx];
          // Foto appare quando siamo passati il suo turno:
          // In phase 0: mostro solo la prima
          // In phase 0.5: mostro 2
          // In phase 0.75: mostro 3
          // In phase >=1: tutte e 4
          const originalPosition = INITIAL_ORDER.indexOf(originalIdx);
          const isVisible = phase >= 0.9 || (phase >= 0.75 && originalPosition < 3)
            || (phase >= 0.5 && originalPosition < 2)
            || (phase >= 0 && originalPosition < 1);
          return (
            <motion.div
              key={originalIdx}
              layout
              transition={{ type: "spring", stiffness: 320, damping: 28 }}
              className="relative aspect-[4/3] rounded-lg overflow-hidden bg-[#F1F4ED] shadow-[0_1px_2px_rgba(28,28,26,0.06)]"
            >
              {isVisible ? (
                <>
                  {/* Before */}
                  <img
                    src={p.before}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                  {/* After crossfade — rallentato e stagger visibile perche'
                      "non si capiva che migliorava". Durata 1.4s per foto,
                      stagger 0.35s → totale ~2.8s da apprezzare il "wow". */}
                  <motion.img
                    src={p.after}
                    alt=""
                    initial={{ opacity: 0 }}
                    animate={{ opacity: showAfter ? 1 : 0 }}
                    transition={{
                      duration: 1.4,
                      delay: showAfter ? gridIdx * 0.35 : 0,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                  {/* iter r83 · Flash bianco che passa sopra la foto quando
                      arriva il turno del crossfade. Rende visibile "sta
                      succedendo qualcosa" senza far percepire ritardo. */}
                  {showAfter && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 0.55, 0] }}
                      transition={{
                        duration: 0.6,
                        delay: gridIdx * 0.35,
                        times: [0, 0.3, 1],
                        ease: "easeOut",
                      }}
                      className="absolute inset-0 bg-white pointer-events-none"
                    />
                  )}
                  {/* iter r84 · Etichette PRIMA/DOPO su ogni foto rimosse.
                      Il banner in fondo "Guarda che differenza" bastava — le
                      4 etichette ripetute risultavano ridondanti (feedback
                      user r83). */}
                  {/* Room label pill (durante phase 0 · sta scattando) */}
                  {phase < 1 && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      className="absolute bottom-1 left-1 bg-black/50 backdrop-blur-sm text-white text-[8.5px] font-semibold rounded px-1.5 py-0.5"
                    >
                      {p.room}
                    </motion.div>
                  )}
                  {/* iter r85 · Badge numerati rimossi. Facevano sembrare
                      le foto "selezionate" (feedback user), invece
                      dovrebbero solo riordinarsi in silenzio. Il layout
                      `layout` di framer-motion gia' fa vedere il
                      riposizionamento delle foto in modo elegante. */}
                </>
              ) : (
                // Placeholder vuoto
                <div className="w-full h-full flex items-center justify-center opacity-30">
                  <Camera size={14} className="text-[#9A968B]" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* CTA row · cambia in base a phase */}
      <div className="mt-auto">
        <AnimatePresence mode="wait">
          {phase < 1 && (
            // Sta scattando · REC circle + text
            <motion.div
              key="capturing"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="bg-[#1C1C1A] text-white text-[11px] font-semibold rounded-md py-2.5 flex items-center justify-center gap-2"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="w-2.5 h-2.5 rounded-full bg-[#C13533]"
              />
              Sto scattando · {shotsCount}/4
            </motion.div>
          )}
          {phase === 1 && (
            <motion.button
              key="btn-migliora"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="w-full bg-[#7C8B6E] text-white text-[11.5px] font-semibold rounded-md py-2.5 flex items-center justify-center gap-1.5 shadow-[0_2px_0_rgba(124,139,110,0.25)]"
            >
              <Sparkle size={12} weight="fill" />
              Migliora le foto
            </motion.button>
          )}
          {phase === 2 && (
            <motion.div
              key="progress"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white border border-[#EAE7DF] rounded-md px-3 py-2.5"
            >
              <div className="flex items-center justify-between text-[10px] text-[#3F3D38] mb-1.5">
                <span className="inline-flex items-center gap-1 font-semibold">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                    className="inline-flex"
                  >
                    <Sparkle size={10} weight="fill" className="text-[#7C8B6E]" />
                  </motion.span>
                  Sto migliorando le foto…
                </span>
                <span className="tabular-nums text-[#9A968B]">3/4</span>
              </div>
              <div className="h-1 bg-[#EAE7DF] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.2, ease: "easeInOut" }}
                  className="h-full bg-[#7C8B6E]"
                />
              </div>
            </motion.div>
          )}
          {phase === 3 && (
            <motion.div
              key="showcase"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="w-full bg-[#F1F4ED] border border-[#7C8B6E]/40 text-[#5A6A4D] text-[11px] font-semibold rounded-md py-2.5 flex items-center justify-center gap-1.5"
            >
              <Sparkle size={11} weight="fill" />
              <motion.span
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
              >
                Guarda che differenza · prima → dopo
              </motion.span>
            </motion.div>
          )}
          {phase === 4 && (
            <motion.button
              key="btn-crea"
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="w-full bg-[#1C1C1A] text-white text-[12px] font-semibold rounded-md py-3 flex items-center justify-center gap-1.5 shadow-[0_2px_0_rgba(28,28,26,0.25)]"
            >
              <Sparkle size={13} weight="fill" />
              Crea annuncio
              <ArrowRight size={12} weight="bold" className="ml-0.5" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Scene · GENERA (mini transizione con spinner)                       */
/* ------------------------------------------------------------------ */
function SceneGenerate() {
  return (
    <motion.div
      key="generate"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 flex flex-col items-center justify-center bg-white"
      data-testid="sst-scene-generate"
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        className="w-10 h-10 rounded-full border-[3px] border-[#F1F4ED] border-t-[#7C8B6E]"
      />
      <div className="mt-4 text-[13px] font-semibold text-[#1C1C1A]">
        Sto creando l&apos;annuncio…
      </div>
      <div className="mt-1 text-[10.5px] text-[#6B6A65] text-center max-w-[220px]">
        Scheda, foto, prezzo, vetrina — pronti in un attimo.
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Scene · VETRINA (final)                                             */
/* ------------------------------------------------------------------ */
function SceneVetrina() {
  return (
    <motion.div
      key="vetrina"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="absolute inset-0 px-3.5 pt-2 pb-3 flex flex-col overflow-hidden"
      data-testid="sst-scene-vetrina"
    >
      {/* Success pill top */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="mb-2 flex justify-center"
      >
        <span className="inline-flex items-center gap-1 bg-[#F1F4ED] text-[#5A6A4D] text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full">
          <Check size={9} weight="bold" />
          Annuncio pronto
        </span>
      </motion.div>

      {/* Agent strip */}
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.35 }}
        className="flex items-center gap-2 mb-2 pb-2 border-b border-[#EAE7DF]"
      >
        <div className="w-6 h-6 rounded-full bg-[#7C8B6E] text-white text-[10px] font-bold inline-flex items-center justify-center shrink-0">
          MR
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-[10.5px] font-bold text-[#1C1C1A] truncate">
            Mario Rossi
          </div>
          <div className="text-[9px] text-[#6B6A65] truncate">
            Casa Immobiliare · Milano
          </div>
        </div>
        <span className="text-[8.5px] uppercase tracking-[0.14em] font-bold text-[#5A6A4D] bg-[#F1F4ED] px-1.5 py-0.5 rounded">
          Vetrina
        </span>
      </motion.div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
        className="relative rounded-md overflow-hidden mb-2 aspect-[16/10]"
      >
        <img
          src={PHOTOS[0].after}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.35 }}
          className="absolute bottom-1.5 left-2 text-white"
        >
          <div className="text-[9px] uppercase tracking-[0.18em] opacity-85 font-semibold">
            Prezzo richiesto
          </div>
          <div className="text-[15px] font-bold leading-none tabular-nums">
            € 245.000
          </div>
        </motion.div>
        <div className="absolute top-1.5 right-1.5 bg-black/45 backdrop-blur-sm text-white text-[9px] font-semibold rounded px-1.5 py-0.5 inline-flex items-center gap-1">
          <Camera size={9} weight="fill" />
          4
        </div>
      </motion.div>

      {/* Titolo + posizione */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.35 }}
        className="mb-2"
      >
        <div className="text-[13px] font-bold text-[#1C1C1A] leading-tight">
          Trilocale ristrutturato · Brera
        </div>
        <div className="text-[10px] text-[#6B6A65] mt-0.5 inline-flex items-center gap-1">
          <MapPin size={9} weight="fill" className="text-[#7C8B6E]" />
          Via Solferino · Milano
        </div>
      </motion.div>

      {/* Specs */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        className="grid grid-cols-4 gap-1 mb-2.5 text-center"
      >
        {[
          { v: "95", u: "mq" },
          { v: "3", u: "locali" },
          { v: "2", u: "bagni" },
          { v: "3°", u: "piano" },
        ].map((s, i) => (
          <div key={i} className="bg-[#F1F4ED] rounded px-1 py-1.5 border border-[#EAE7DF]">
            <div className="text-[12px] font-bold text-[#1C1C1A] leading-none">{s.v}</div>
            <div className="text-[8.5px] text-[#6B6A65] uppercase tracking-wider mt-0.5">{s.u}</div>
          </div>
        ))}
      </motion.div>

      {/* Gallery 3 thumbs (le altre stanze) */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65, duration: 0.3 }}
        className="grid grid-cols-3 gap-1 mb-2.5"
      >
        {[1, 2, 3].map((idx, i) => (
          <div key={i} className="aspect-[4/3] rounded overflow-hidden bg-[#F1F4ED]">
            <img
              src={PHOTOS[idx].after}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
          </div>
        ))}
      </motion.div>

      {/* Publish row */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.3 }}
        className="mt-auto"
      >
        <div className="flex items-center justify-between text-[8.5px] uppercase tracking-[0.16em] text-[#9A968B] font-bold mb-1.5">
          <span>Pubblica su</span>
          <Share size={9} weight="bold" />
        </div>
        <div className="flex gap-1">
          {["Immobiliare", "Idealista", "Instagram", "WhatsApp"].map((lab, i) => (
            <motion.span
              key={lab}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9 + i * 0.06, duration: 0.22 }}
              className="flex-1 bg-white border border-[#EAE7DF] text-[9px] text-[#3F3D38] font-medium text-center rounded py-1.5 leading-none"
            >
              {lab}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Scene · SOCIAL (contenuti Instagram generati automaticamente)       */
/* iter r90 · Rifatta: contenuti PIÙ GRANDI, stile identico ai post   */
/* reali Pronto (banda verde salvia in basso con titolo + prezzo +     */
/* specs). Un'unica card grande che cambia formato: POST 1:1 →         */
/* REEL 9:16 → PRIMA/DOPO 1:1 in loop.                                 */
/* ------------------------------------------------------------------ */
const CAPTION_TEXT = "Trilocale in Brera pronto per te 🏛️ Parquet spina francese, cucina open space, terrazzo. 95 mq · € 245.000";

// Card grafica identica ai post reali Pronto (verde_brera template)
function ProntoPostCard({ photoAfter, badge = "POST 1:1" }) {
  return (
    <div className="relative w-[190px] aspect-square rounded-lg overflow-hidden shadow-2xl border-2 border-white bg-black">
      {/* Foto principale con Ken Burns */}
      <img
        src={photoAfter}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: "sst-kb-post 6s ease-in-out infinite" }}
        loading="lazy"
        draggable={false}
      />
      {/* Banda verde salvia in basso · esattamente come i post reali */}
      <div
        className="absolute inset-x-0 bottom-0 px-2 pt-2 pb-2"
        style={{
          background: "linear-gradient(180deg, rgba(124,139,110,0) 0%, #7C8B6E 30%, #6D7C61 100%)",
        }}
      >
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-white text-[7.5px] uppercase tracking-[0.2em] opacity-90 font-semibold">
              Via Solferino · Milano
            </div>
            <div className="text-white text-[11px] font-bold leading-tight tracking-tight mt-0.5">
              Trilocale
              <br />
              in Brera
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-white/85 text-[6.5px] uppercase tracking-widest">Prezzo</div>
            <div className="text-white text-[13px] font-bold leading-none tabular-nums mt-0.5">
              €245K
            </div>
          </div>
        </div>
        {/* Specs pills · come i post reali */}
        <div className="flex items-center gap-1 mt-1.5">
          {["95 mq", "3 loc.", "2 bagni"].map((s, i) => (
            <span
              key={i}
              className="bg-white/20 text-white text-[6.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm backdrop-blur-sm"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
      {/* Watermark bottom-right stile Pronto */}
      <div className="absolute top-1.5 right-1.5 bg-white/95 text-[#7C8B6E] text-[6px] font-bold uppercase tracking-widest px-1 py-0.5 rounded-sm">
        Pronto
      </div>
      {/* Formato pill top-left */}
      <div className="absolute top-1.5 left-1.5 bg-black/55 backdrop-blur-sm text-white text-[6.5px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm">
        {badge}
      </div>
      {/* Like heart pulsante */}
      <motion.div
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1.5 right-14 text-white"
      >
        <Heart size={12} weight="fill" />
      </motion.div>
    </div>
  );
}

function ProntoReelCard({ photoAfter }) {
  return (
    <div className="relative w-[140px] aspect-[9/16] rounded-lg overflow-hidden shadow-2xl border-2 border-white bg-black">
      <img
        src={photoAfter}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: "sst-kb-reel 4.5s ease-in-out infinite" }}
        loading="lazy"
        draggable={false}
      />
      <div className="absolute inset-0 bg-black/15" />

      {/* Header con progress bars stile IG */}
      <div className="absolute top-1.5 left-1.5 right-1.5 flex items-center gap-0.5">
        {[0, 1, 2].map((k) => (
          <span
            key={k}
            className={`flex-1 h-[1.5px] rounded-full ${k === 0 ? "bg-white" : "bg-white/40"}`}
          />
        ))}
      </div>

      {/* Play icon centrale */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center shadow-lg"
        >
          <Play size={16} weight="fill" className="text-[#1C1C1A] translate-x-[1px]" />
        </motion.div>
      </div>

      {/* Info bottom · titolo + prezzo · stile reel */}
      <div className="absolute inset-x-0 bottom-0 px-2 pt-4 pb-2 bg-gradient-to-t from-black/85 to-transparent">
        <div className="text-white text-[7px] uppercase tracking-widest opacity-90 font-semibold">
          Trilocale · Brera
        </div>
        <div className="flex items-end justify-between mt-0.5">
          <div className="text-white text-[10px] font-bold leading-none">
            € 245.000
          </div>
          <div className="text-white/80 text-[6.5px] font-mono">0:15</div>
        </div>
      </div>

      {/* Formato pill */}
      <div className="absolute top-3 left-1.5 bg-black/60 backdrop-blur-sm text-white text-[6.5px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm">
        REEL 9:16
      </div>
    </div>
  );
}

function ProntoOpenHouseCard({ photoAfter }) {
  // Replica il vero post Open House layout Elegante:
  // filtro grigio scuro sulla foto + overline "TI INVITIAMO" con
  // linee decorative + titolo "Open House" italic serif enorme +
  // giorno / data · ora + indirizzo maiuscolo. Sotto banda con
  // titolo immobile.
  return (
    <div className="relative w-[190px] aspect-square rounded-lg overflow-hidden shadow-2xl border-2 border-white bg-black">
      {/* Foto di sfondo con Ken Burns */}
      <img
        src={photoAfter}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: "sst-kb-post 7s ease-in-out infinite" }}
        loading="lazy"
        draggable={false}
      />
      {/* Filtro grigio scuro come nel composer reale */}
      <div className="absolute inset-0 bg-[#1C1C1A]/45" />

      {/* Contenuto centrato */}
      <div className="relative h-full flex flex-col items-center justify-center px-3 text-center text-[#FAFAF7]">
        {/* Overline con linee decorative gold */}
        <div className="flex items-center gap-1.5 mb-1">
          <span className="h-[1px] w-4 bg-[#C8B486]" />
          <span className="text-[7px] font-bold uppercase tracking-[0.28em] text-[#C8B486]">
            Ti invitiamo
          </span>
          <span className="h-[1px] w-4 bg-[#C8B486]" />
        </div>

        {/* Titolo Open House · italic serif grande */}
        <div
          className="font-editorial italic leading-[0.88] text-[34px] mt-0.5"
          style={{ fontFamily: '"Cormorant Garamond", serif' }}
        >
          Open
          <br />
          House
        </div>

        {/* Piccola linea gold sotto */}
        <div className="w-8 h-[1px] bg-[#C8B486] my-1.5" />

        {/* Giorno */}
        <div className="text-[8.5px] font-bold uppercase tracking-[0.24em]">
          Domenica
        </div>
        {/* Data · ora — font italic serif per stile Elegante */}
        <div
          className="italic text-[13px] leading-none mt-0.5"
          style={{ fontFamily: '"Cormorant Garamond", serif' }}
        >
          14 giugno · ore 15:00
        </div>

        {/* Indirizzo maiuscolo · font più piccolo */}
        <div className="text-[7px] font-semibold uppercase tracking-[0.22em] mt-1.5 opacity-90">
          Via Solferino · Milano
        </div>
      </div>

      {/* Banda inferiore verde salvia con titolo · come nei post veri */}
      <div
        className="absolute inset-x-0 bottom-0 px-2 py-1.5 flex items-center justify-between gap-2"
        style={{ background: "linear-gradient(180deg, rgba(124,139,110,0.85) 0%, #6D7C61 100%)" }}
      >
        <div className="text-white text-[7.5px] font-bold uppercase tracking-widest leading-tight">
          Trilocale · Brera
        </div>
        <div className="text-white text-[8.5px] font-bold tabular-nums">
          €245K
        </div>
      </div>

      {/* Formato pill top-left */}
      <div className="absolute top-1.5 left-1.5 bg-white/95 text-[#7C8B6E] text-[6.5px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm">
        Open House
      </div>
    </div>
  );
}

function SceneSocial() {
  const [typed, setTyped] = useState("");
  const [formatIdx, setFormatIdx] = useState(0);

  // typewriter effect for caption
  useEffect(() => {
    let i = 0;
    const iv = setInterval(() => {
      i += 2;
      if (i >= CAPTION_TEXT.length) {
        setTyped(CAPTION_TEXT);
        clearInterval(iv);
      } else {
        setTyped(CAPTION_TEXT.slice(0, i));
      }
    }, 40);
    return () => clearInterval(iv);
  }, []);

  // Rotazione tra i 3 formati · ognuno visibile ~2.2s
  useEffect(() => {
    const timers = [
      setTimeout(() => setFormatIdx(1), 2200),
      setTimeout(() => setFormatIdx(2), 4400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const formats = [
    { key: "post",       label: "Post 1:1" },
    { key: "reel",       label: "Reel 9:16" },
    { key: "openhouse",  label: "Open House" },
  ];

  return (
    <motion.div
      key="social"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="absolute inset-0 px-3.5 pt-2 pb-3 flex flex-col overflow-hidden"
      data-testid="sst-scene-social"
    >
      <style>{`
        @keyframes sst-kb-post { 0%,100% { transform: scale(1); } 50% { transform: scale(1.1) translate(-2%, -1%); } }
        @keyframes sst-kb-reel { 0%,100% { transform: scale(1.03); } 50% { transform: scale(1.13) translate(1%, -3%); } }
      `}</style>

      {/* Success pill top */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="mb-1.5 flex justify-center"
      >
        <span className="inline-flex items-center gap-1 bg-[#FCEEF3] text-[#E1306C] text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full">
          <InstagramLogo size={9} weight="fill" />
          Contenuti social pronti
        </span>
      </motion.div>

      {/* Tab indicator · minimale · mostra su quale formato siamo */}
      <div className="flex items-center justify-center gap-1 mb-2">
        {formats.map((f, i) => (
          <motion.span
            key={f.key}
            animate={{
              width: i === formatIdx ? 20 : 6,
              backgroundColor: i === formatIdx ? "#7C8B6E" : "#EAE7DF",
            }}
            transition={{ duration: 0.4 }}
            className="h-1 rounded-full"
          />
        ))}
      </div>

      {/* Card grande · morph tra i 3 formati */}
      <div className="relative flex items-center justify-center mb-2" style={{ height: "215px" }}>
        <AnimatePresence mode="wait">
          {formatIdx === 0 && (
            <motion.div
              key="fmt-post"
              initial={{ opacity: 0, scale: 0.85, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -12 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <ProntoPostCard photoAfter={PHOTOS[0].after} badge="POST 1:1" />
            </motion.div>
          )}
          {formatIdx === 1 && (
            <motion.div
              key="fmt-reel"
              initial={{ opacity: 0, scale: 0.85, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -12 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <ProntoReelCard photoAfter={PHOTOS[1].after} />
            </motion.div>
          )}
          {formatIdx === 2 && (
            <motion.div
              key="fmt-openhouse"
              initial={{ opacity: 0, scale: 0.85, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -12 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <ProntoOpenHouseCard photoAfter={PHOTOS[2].after} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Caption typewriter · piu' compatta */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.35 }}
        className="bg-[#F8F8F5] border border-[#EAE7DF] rounded-md px-2 py-1.5 mb-2 min-h-[40px]"
      >
        <div className="text-[9.5px] text-[#1C1C1A] leading-tight line-clamp-2">
          {typed}
          <motion.span
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 0.7, repeat: Infinity }}
            className="inline-block w-[1px] h-2.5 bg-[#1C1C1A] ml-[1px] align-middle"
          />
        </div>
      </motion.div>

      {/* Bottone Pubblica */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.35 }}
        className="mt-auto"
      >
        <div className="w-full bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-[11px] font-semibold rounded-md py-2 text-center flex items-center justify-center gap-1.5 shadow-md">
          <InstagramLogo size={12} weight="fill" />
          Pubblica su Instagram
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* MAIN component                                                       */
/* ------------------------------------------------------------------ */

/* Scene 6 · SERVIZI · iter r99l · User M1527 · iter 2026-02 (+ Rendering)
 * Griglia 2 col di 11 servizi (10 + Rendering & Home Staging in pos 2).
 * Layout: icona colorata cerchio + titolo bold + micro descrizione.
 * Appaiono staggered (120ms tra ognuna) — timing leggermente accelerato
 * rispetto ai 140ms precedenti per stare comunque dentro gli 8s della
 * scena finale con 11 item invece di 10.
 * Row-gap ridotto (2.5→2) per bilanciare visivamente l'11º item sull'ultima row.
 */
function SceneServizi() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="absolute inset-0 flex flex-col px-2.5 py-3 bg-white"
      data-testid="sst-scene-servizi"
    >
      {/* Header piccolo */}
      <div className="text-center mb-2.5">
        <div className="text-[8px] tracking-[0.22em] text-[#9A968B] uppercase mb-0.5">
          Un'unica app
        </div>
        <div className="text-[13px] font-editorial text-[#1C1C1A] leading-tight">
          Per l'agente <span className="italic text-[#7C8B6E]">immobiliare</span>
        </div>
      </div>

      {/* Griglia 2 col · icone staggered · 11 items */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-2.5 content-start">
        {SERVIZI.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.12 + i * 0.12,
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="flex items-start gap-1.5"
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: `${s.color}16`,
                  border: `1.5px solid ${s.color}`,
                }}
              >
                <Icon size={18} weight="bold" color={s.color} />
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="text-[9.5px] font-bold text-[#1C1C1A] leading-tight">
                  {s.title}
                </div>
                <div className="text-[7.5px] text-[#6B6A65] leading-snug mt-0.5">
                  {s.desc}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer soft */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.12 + SERVIZI.length * 0.12 + 0.25, duration: 0.4 }}
        className="mt-auto pt-2 text-center"
      >
        <div className="text-[9px] tracking-[0.2em] text-[#7C8B6E] uppercase font-semibold">
          Prontoimmo.it
        </div>
      </motion.div>
    </motion.div>
  );
}


export default function SopralluogoStoryPreview() {
  // scene:  "record" | "photo" | "generate" | "vetrina" | "social" | "servizi"
  const [scene, setScene] = useState("record");
  const [photoPhase, setPhotoPhase] = useState(0);
  const [seconds, setSeconds] = useState(120);
  // iter 2026-02 · Audio RIMOSSO su richiesta utente («togli i suoni, basta»).
  // Nessuna importazione da landingSound.js, nessun toggle, nessun playSfx.

  // Stepper progression (5 step):
  //  record scene              → stepIdx = 0 (Racconta)
  //  photo scene               → stepIdx = 1 (Foto)
  //  generate + vetrina scene  → stepIdx = 2 (Vetrina)
  //  social scene              → stepIdx = 3 (Social)
  //  servizi scene             → stepIdx = 4 (Servizi)
  const stepIdx = scene === "record"
    ? 0
    : scene === "photo"
    ? 1
    : scene === "social"
    ? 3
    : scene === "servizi"
    ? 4
    : 2;

  // Prime dell'audio manager al mount + subscribe a mute state.
  // primeAudio() precarica gli asset e installa il listener di unlock:
  // il suono NON parte finché l'utente non ha almeno cliccato/scrollato.
  // → RIMOSSO 2026-02: nessun audio nella landing.

  useEffect(() => {
    let timers = [];
    const run = () => {
      // RESET
      setScene("record");
      setPhotoPhase(0);
      setSeconds(120);

      // iter 2026-02 · AUDIO RIMOSSO su richiesta utente. Tutte le scene
      // avanzano in silenzio — la demo si affida solo alle micro-animazioni.

      // Scene 1 · RACCONTA · 0 - 4s
      // Scene 2 · FOTOGRAFA · 4s - 19.3s
      timers.push(setTimeout(() => setScene("photo"), 4000));
      timers.push(setTimeout(() => setPhotoPhase(0.5),  5200));
      timers.push(setTimeout(() => setPhotoPhase(0.75), 6400));
      timers.push(setTimeout(() => setPhotoPhase(0.9),  7600));
      timers.push(setTimeout(() => setPhotoPhase(1),    8800));   // tap "Migliora"
      timers.push(setTimeout(() => setPhotoPhase(2),   11300));
      timers.push(setTimeout(() => setPhotoPhase(3),   14300));   // reveal foto migliorata
      timers.push(setTimeout(() => setPhotoPhase(4),   16800));   // tap "Crea annuncio"

      // Scene 3 · GENERA · 19.3s - 20.3s
      timers.push(setTimeout(() => setScene("generate"), 19300));
      // Scene 4 · VETRINA · 20.3s - 24.3s
      timers.push(setTimeout(() => setScene("vetrina"),  20300));
      // Scene 5 · SOCIAL · 24.3s - 31.3s
      timers.push(setTimeout(() => setScene("social"),   24300));
      // Scene 6 · SERVIZI · 31.3s - 39.3s
      timers.push(setTimeout(() => setScene("servizi"),  31300));

      // Loop restart · 40s
      timers.push(setTimeout(run, 40000));
    };
    run();
    return () => timers.forEach(clearTimeout);
  }, []);

  // Timer 2:00 → 2:35 durante scene record
  useEffect(() => {
    if (scene !== "record") return;
    const iv = setInterval(() => {
      setSeconds((s) => (s >= 155 ? s : s + 1));
    }, 100);
    return () => clearInterval(iv);
  }, [scene]);

  return (
    <div
      className="absolute top-[44px] left-0 right-0 bottom-0 bg-white text-[#1C1C1A] overflow-hidden"
      data-testid="sopralluogo-story-preview"
    >
      <style>{`
        @keyframes sst-bar {
          0% { transform: scaleY(0.35); }
          100% { transform: scaleY(1.25); }
        }
      `}</style>

      {/* Stepper sempre visibile in cima */}
      <div className="px-3.5 pt-2 pb-1.5 bg-white border-b border-[#F1F4ED] relative z-10 overflow-hidden">
        <Stepper stepIdx={stepIdx} />
      </div>

      {/* Scene container · lascia posto per lo stepper */}
      <div className="absolute top-[42px] left-0 right-0 bottom-0 overflow-hidden">
        <AnimatePresence mode="wait">
          {scene === "record" && <SceneRecord key="record" seconds={seconds} />}
          {scene === "photo" && <ScenePhoto key="photo" phase={photoPhase} />}
          {scene === "generate" && <SceneGenerate key="generate" />}
          {scene === "vetrina" && <SceneVetrina key="vetrina" />}
          {scene === "social" && <SceneSocial key="social" />}
          {scene === "servizi" && <SceneServizi key="servizi" />}
        </AnimatePresence>
      </div>
    </div>
  );
}
